import type { CategoryDef } from '../categories';
import { CATEGORIES } from '../categories';
import { builtinRegistration } from './builtins';
import { gpioRegistration } from './gpio';
import { analogRegistration } from './analog';
import { timeRegistration } from './time';
import { commsRegistration } from './comms';
import { networkRegistration } from './network';
import { servoRegistration } from './servo';
import { motorRegistration } from './motor';
import { displayRegistration } from './display';
import { lanRegistration } from './lan';
import { variablesRegistration } from './variables';
import { registerBlocks, getBlockMeta } from '../registry';

export const ALL_REGISTRATIONS = [
  builtinRegistration,
  gpioRegistration,
  analogRegistration,
  timeRegistration,
  commsRegistration,
  networkRegistration,
  servoRegistration,
  motorRegistration,
  displayRegistration,
  lanRegistration,
  variablesRegistration,
];

let registered = false;

export function registerAllBlocks(): void {
  if (registered) return;
  registered = true;
  for (const r of ALL_REGISTRATIONS) registerBlocks(r);
}

/* ------------------------------------------------------------------ */
/* Toolbox                                                             */
/* ------------------------------------------------------------------ */

/** Block order per category. Built-ins live beside the custom blocks. */
/** A toolbox entry is a block type, optionally with pre-shaped mutator state. */
type ToolboxEntry = string | { type: string; extraState: Record<string, unknown> };

const TOOLBOX_LAYOUT: Record<string, ToolboxEntry[]> = {
  control: [
    'esp32_on_setup',
    'esp32_on_loop',
    'controls_if',
    'esp32_if_else',
    'esp32_if_elseif_else',
    'controls_repeat_ext',
    'controls_whileUntil',
    'controls_for',
    'controls_flow_statements',
    'esp32_every_ms',
  ],
  gpio: ['esp32_pin_mode', 'esp32_digital_write', 'esp32_pin_toggle', 'esp32_digital_read'],
  analog: ['esp32_analog_read', 'esp32_analog_write', 'esp32_pwm_setup', 'esp32_pwm_write', 'esp32_pwm_release'],
  interrupts: ['esp32_on_interrupt', 'esp32_interrupt_detach'],
  time: ['esp32_delay_ms', 'esp32_delay_us', 'esp32_millis', 'esp32_micros'],
  power: ['esp32_deep_sleep_timer', 'esp32_deep_sleep_ext', 'esp32_wakeup_cause'],
  comms: [
    'esp32_serial_begin',
    'esp32_serial_print',
    'esp32_serial_available',
    'esp32_serial_read_line',
    'esp32_uart2_begin',
    'esp32_uart2_print',
    'esp32_uart2_available',
    'esp32_uart2_read_byte',
    'esp32_i2c_begin',
    'esp32_i2c_write_register',
    'esp32_i2c_read_register',
    'esp32_i2c_scan',
    'esp32_spi_begin',
    'esp32_spi_transfer',
  ],
  network: [
    'esp32_wifi_connect',
    'esp32_wifi_connected',
    'esp32_wifi_local_ip',
    'esp32_wifi_disconnect',
    'esp32_http_get',
    'esp32_http_post_json',
    'esp32_mqtt_setup',
    'esp32_mqtt_publish',
    'esp32_mqtt_subscribe',
    'esp32_mqtt_loop',
    'esp32_mqtt_connected',
    'esp32_on_mqtt_message',
    'esp32_mqtt_payload',
  ],
  servo: [
    'esp32_servo_attach',
    'esp32_servo_write',
    'esp32_servo_write_us',
    'esp32_servo_read',
    'esp32_servo_detach',
  ],
  motor: ['esp32_motor_setup', 'esp32_motor_run', 'esp32_motor_stop'],
  display: [
    'esp32_oled_setup',
    'esp32_tft_setup',
    'esp32_display_fill',
    'esp32_display_clear',
    'esp32_display_text',
    'esp32_display_pixel',
    'esp32_display_line',
    'esp32_display_rect',
    'esp32_display_show',
  ],
  lan: [
    'esp32_eth_begin',
    'esp32_eth_begin_static',
    'esp32_eth_connected',
    'esp32_eth_local_ip',
    'esp32_eth_maintain',
  ],
  logic: ['logic_compare', 'logic_operation', 'logic_negate', 'logic_boolean'],
  math: ['math_number', 'math_arithmetic', 'math_modulo', 'math_random_int', 'esp32_map', 'esp32_constrain'],
  variables: ['variables_get', 'variables_set', 'math_change'],
  globals: ['esp32_variable_declare', 'esp32_global_read', 'esp32_global_write'],
  text: ['text', 'esp32_text_join'],
};

/** Default shadow values so value sockets are never empty on the canvas. */
const SHADOW_NUMBERS: Record<string, Record<string, number>> = {
  esp32_analog_write: { DUTY: 200 },
  esp32_pwm_write: { DUTY: 128 },
  esp32_delay_ms: { DURATION: 1000 },
  esp32_delay_us: { DURATION: 100 },
  esp32_deep_sleep_timer: { SECONDS: 10 },
  esp32_i2c_write_register: { ADDR: 0x76, REG: 0, VALUE: 1 },
  esp32_i2c_read_register: { ADDR: 0x76, REG: 0 },
  esp32_spi_transfer: { DATA: 65 },
  controls_repeat_ext: { TIMES: 10 },
  esp32_servo_write: { ANGLE: 90 },
  esp32_servo_write_us: { US: 1500 },
  esp32_motor_run: { SPEED: 200 },
  esp32_display_text: { X: 0, Y: 0 },
  esp32_display_pixel: { X: 0, Y: 0 },
  esp32_display_line: { X1: 0, Y1: 0, X2: 60, Y2: 30 },
  esp32_display_rect: { X: 10, Y: 10, W: 40, H: 20 },
};

const SHADOW_STRINGS: Record<string, Record<string, string>> = {
  esp32_http_get: { URL: 'http://example.com/data' },
  esp32_http_post_json: { URL: 'http://example.com/api', BODY: '{"value":1}' },
  esp32_mqtt_publish: { TOPIC: 'home/status', PAYLOAD: 'hello' },
  esp32_mqtt_subscribe: { TOPIC: 'home/led' },
  esp32_serial_print: { VALUE: 'Hello ESP32' },
  esp32_uart2_print: { VALUE: 'Hello UART2' },
  esp32_text_join: { A: 'value: ', B: '42' },
  esp32_global_write: { VALUE: '0' },
  esp32_display_text: { TEXT: 'Hello!' },
};

function blockEntry(entry: ToolboxEntry): Record<string, unknown> | null {
  const type = typeof entry === 'string' ? entry : entry.type;
  const meta = getBlockMeta(type);
  if (!meta) return null;
  const out: Record<string, unknown> = { kind: 'block', type };
  if (typeof entry !== 'string') out.extraState = entry.extraState;
  const num = SHADOW_NUMBERS[type];
  const str = SHADOW_STRINGS[type];
  if (num || str) {
    const inputs: Record<string, unknown> = {};
    for (const [name, value] of Object.entries(num ?? {})) {
      inputs[name] = { shadow: { type: 'math_number', fields: { NUM: value } } };
    }
    for (const [name, value] of Object.entries(str ?? {})) {
      inputs[name] = { shadow: { type: 'text', fields: { TEXT: value } } };
    }
    out.inputs = inputs;
  }
  return out;
}

export interface ToolboxCategory {
  kind: 'category';
  name: string;
  colour: string;
  contents: Record<string, unknown>[];
}

export function buildToolbox(): ToolboxCategory[] {
  const byCategory = new Map<string, ToolboxCategory>();
  const ensure = (key: string, name: string, colour: string): ToolboxCategory => {
    let cat = byCategory.get(key);
    if (!cat) {
      cat = { kind: 'category', name, colour, contents: [] };
      byCategory.set(key, cat);
    }
    return cat;
  };

  // Fixed display order follows CATEGORIES, then the extra Globals rail.
  const rails: { key: string; def: CategoryDef | undefined }[] = [
    ...CATEGORIES.filter((c) => TOOLBOX_LAYOUT[c.id] && c.id !== 'variables').map((def) => ({
      key: def.id,
      def,
    })),
    { key: 'variables', def: CATEGORIES.find((c) => c.id === 'variables') },
    { key: 'globals', def: undefined },
  ];

  const out: ToolboxCategory[] = [];
  for (const { key, def } of rails) {
    if (key === 'variables') {
      // Blockly's dynamic variable category (create/rename/delete built in).
      out.push({
        kind: 'category',
        name: 'Variables',
        colour: def?.colour ?? '#A87900',
        contents: [],
        // custom is not part of this narrow type; patch on assignment below.
        ...({ custom: 'VARIABLE' } as object),
      });
      continue;
    }
    const cat = ensure(key, def?.label ?? (key === 'globals' ? 'Globals' : key), def?.colour ?? '#888888');
    for (const type of TOOLBOX_LAYOUT[key] ?? []) {
      const entry = blockEntry(type);
      if (entry) cat.contents.push(entry);
    }
    if (cat.contents.length) out.push(cat);
  }
  return out;
}
